import localDataService from "./data/localDataService";
import { GeolocationData } from "../domain/models";

const key = "geolocation";

let _intervalId: number | null = null;

const _fetchCurrentPosition = (
  options?: PositionOptions
): Promise<GeolocationPosition> => {
  const opts = { enableHighAccuracy: true, timeout: 15000, ...options };

  return new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, opts)
  );
};

const _storeCurrentPosition = (position: GeolocationPosition | null) => {
  if (!position) {
    localDataService.removeItem(key);
    return;
  }
  const { coords, timestamp } = position;
  const value = {
    accuracy: coords.accuracy,
    altitude: coords.altitude,
    altitudeAccuracy: coords.altitudeAccuracy,
    heading: coords.heading,
    latitude: coords.latitude,
    longitude: coords.longitude,
    speed: coords.speed,
    timestamp,
  };
  localDataService.setItem(key, value);
};

const stopAutoPositionUpdate = () => {
  if (_intervalId !== null) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
};

const _handleGeolocationError = (error: GeolocationPositionError) => {
  if (error.code === error.PERMISSION_DENIED) {
    _storeCurrentPosition(null);
    stopAutoPositionUpdate();
    return;
  }

  _storeCurrentPosition(null);
};

const startAutoPositionUpdate = (options?: PositionOptions) => {
  // Don't start if already running
  if (_intervalId !== null) return;

  _fetchCurrentPosition(options)
    .then(_storeCurrentPosition)
    .catch(_handleGeolocationError);

  _intervalId = window.setInterval(() => {
    _fetchCurrentPosition(options)
      .then(_storeCurrentPosition)
      .catch(_handleGeolocationError);
  }, 5000);
};

const initGeolocationWatcher = (options?: PositionOptions) => {
  if (!("permissions" in navigator) || !navigator.permissions?.query) {
    startAutoPositionUpdate(options);
    return;
  }

  navigator.permissions
    .query({ name: "geolocation" as PermissionName })
    .then((status) => {
      // If already granted, start immediately
      if (status.state === "granted") {
        startAutoPositionUpdate(options);
      }

      status.onchange = () => {
        if (status.state === "granted") {
          startAutoPositionUpdate(options);
        } else if (status.state === "denied") {
          stopAutoPositionUpdate();
          _storeCurrentPosition(null);
        }
      };
    })
    .catch(() => {
      startAutoPositionUpdate(options);
    });
};

const dispose = () => {
  localDataService.removeItem(key);
  stopAutoPositionUpdate();
};

const getCurrentPosition = (): GeolocationData | null => {
  return localDataService.getItem(key, JSON.parse);
};

const geolocationService = {
  initGeolocationWatcher,
  startAutoPositionUpdate,
  stopAutoPositionUpdate,
  dispose,
  getCurrentPosition,
};

export default geolocationService;
